import { useState, useEffect, useCallback } from 'react';
import { accountsApi } from '../api';
import { Account } from '../types';

const STORAGE_KEY_PREFIX = 'tallai:account:';
const ACCOUNT_CHANGE_EVENT = 'tallai:account-change';

export interface UseAccountPreferenceResult {
  selectedAccountId: string | null;
  setSelectedAccountId: (accountId: string | null) => void;
  accounts: Account[];
  isLoading: boolean;
}

/**
 * Hook to manage account selection with localStorage persistence.
 * Fetches accounts for a client and persists the selected account ID.
 * Auto-selects the first account if no preference is saved.
 *
 * @param clientId - The client ID to fetch accounts for
 * @returns Object containing selectedAccountId, setSelectedAccountId, accounts, and isLoading
 */
export function useAccountPreference(clientId: string | undefined): UseAccountPreferenceResult {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const storageKey = clientId ? `${STORAGE_KEY_PREFIX}${clientId}` : null;

  // Load accounts and restore preference from localStorage
  useEffect(() => {
    if (!clientId) {
      setAccounts([]);
      setSelectedAccountIdState(null);
      setIsLoading(false);
      return;
    }

    const fetchAccounts = async () => {
      try {
        setIsLoading(true);
        const data = await accountsApi.listAccounts(clientId);
        setAccounts(data);

        // Try to restore saved preference
        const savedAccountId = storageKey ? localStorage.getItem(storageKey) : null;

        if (savedAccountId && data.some((a) => a.id === savedAccountId)) {
          // Saved preference is still valid
          setSelectedAccountIdState(savedAccountId);
        } else if (data.length > 0) {
          // Auto-select first account
          setSelectedAccountIdState(data[0].id);
        } else {
          setSelectedAccountIdState(null);
        }
      } catch (err) {
        console.error('Failed to load accounts:', err);
        setAccounts([]);
        setSelectedAccountIdState(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccounts();
  }, [clientId, storageKey]);

  // Listen for account changes from other hook instances
  useEffect(() => {
    const handleAccountChange = (event: CustomEvent<{ clientId: string; accountId: string | null }>) => {
      if (event.detail.clientId === clientId) {
        setSelectedAccountIdState(event.detail.accountId);
      }
    };

    window.addEventListener(ACCOUNT_CHANGE_EVENT, handleAccountChange as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_CHANGE_EVENT, handleAccountChange as EventListener);
    };
  }, [clientId]);

  // Wrapper to persist selection to localStorage and notify other instances
  const setSelectedAccountId = useCallback(
    (accountId: string | null) => {
      setSelectedAccountIdState(accountId);

      if (storageKey) {
        if (accountId) {
          localStorage.setItem(storageKey, accountId);
        } else {
          localStorage.removeItem(storageKey);
        }
      }

      // Notify other hook instances of the change
      if (clientId) {
        window.dispatchEvent(
          new CustomEvent(ACCOUNT_CHANGE_EVENT, {
            detail: { clientId, accountId },
          })
        );
      }
    },
    [storageKey, clientId]
  );

  return {
    selectedAccountId,
    setSelectedAccountId,
    accounts,
    isLoading,
  };
}
